import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getFaculties, getSections, getServices, createTicket, getTicket, initCSRF } from '../services/api';
import './StudentKiosk.css';

function StudentKiosk() {
  const [step, setStep] = useState(1);
  const [faculties, setFaculties] = useState([]);
  const [sections, setSections] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [studentId, setStudentId] = useState('');
  const [generatedTicket, setGeneratedTicket] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    initCSRF();
    loadFaculties();
  }, []);

  // Poll for QR scan — when scanned, reset to step 1
  useEffect(() => {
    if (step !== 5 || !generatedTicket?.id) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const res = await getTicket(generatedTicket.id);
        if (!cancelled && res.data?.scanned_at) {
          clearInterval(interval);
          handleReset();
        }
      } catch (e) {
        // non-blocking
      }
    }, 2000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, generatedTicket]);

  const loadFaculties = async () => {
    try {
      const res = await getFaculties();
      setFaculties(res.data.results || res.data);
    } catch (e) {
      toast.error('Fakultetlarni yuklashda xatolik');
    }
  };

  const handleFacultySelect = async (faculty) => {
    setSelectedFaculty(faculty);
    setSelectedSection(null);
    setSelectedService(null);
    setLoading(true);
    try {
      const res = await getSections(faculty.id);
      setSections(res.data.results || res.data);
      setStep(2);
    } catch (e) {
      toast.error("Bo'limlarni yuklashda xatolik");
    } finally {
      setLoading(false);
    }
  };

  const handleSectionSelect = async (section) => {
    setSelectedSection(section);
    setSelectedService(null);
    setLoading(true);
    try {
      const res = await getServices(section.id);
      setServices(res.data.results || res.data);
      setStep(3);
    } catch (e) {
      toast.error('Xizmatlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = async (service) => {
    setSelectedService(service);
    setLoading(true);
    try {
      const res = await createTicket({
        faculty: selectedFaculty.id,
        section: selectedSection.id,
        service: service.id,
        student_name: '',
        student_id: '',
      });
      const ticket = res.data;
      setGeneratedTicket(ticket);
      try {
        const qrRes = await fetch(`/api/tickets/${ticket.id}/qr/`);
        if (qrRes.ok) {
          const qrData = await qrRes.json();
          setQrCode(qrData.qr_code);
        }
      } catch (err) {
        console.error('QR code load failed:', err);
      }
      setStep(5);
      toast.success('Navbat yaratildi!');
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.detail || error.message || 'Navbat yaratishda xatolik';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

const handlePrintTicket = () => {
    const ticket = generatedTicket;
    const qr = qrCode;
    if (!ticket) return;

    const printWindow = window.open('', '', 'width=400,height=600');
    if (!printWindow) {
      toast.error('Pop-up bloklangan. Iltimos pop-up ruxsatini bering.');
      return;
    }

const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Ticket</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Courier New',monospace;width:80mm;background:#fff;text-align:center}
.ticket{width:80mm;padding:6mm 8mm}
.faculty{font-size:11pt;font-weight:bold;margin-bottom:4mm;word-wrap:break-word}
.divider{border-top:1px dashed #000;margin:3mm 0}
.ticket-number{font-size:42pt;font-weight:bold;letter-spacing:3px;line-height:1.1;margin:3mm 0}
.label{font-size:9pt;color:#333;margin-bottom:2mm}
@media print{body{margin:0;width:80mm}}
</style></head><body>
<div class="ticket">
  <div class="faculty">${ticket.faculty_name}</div>
  <div class="divider"></div>
  <div class="label">Navbat raqami</div>
  <div class="ticket-number">${ticket.ticket_number}</div>
  <div class="divider"></div>
</div>
<script>
window.onload=function(){setTimeout(function(){window.print();setTimeout(function(){window.close()},500)},200)};
</script>
</body>
</html>
`;
    printWindow.document.write(html);
    printWindow.document.close();

    // Wait for print window to close, then reset
    const fallback = setTimeout(() => handleReset(), 15000);
    const check = setInterval(() => {
      if (printWindow.closed) {
        clearInterval(check);
        clearTimeout(fallback);
        handleReset();
      }
    }, 500);
  };

  const handleReset = () => {
    setStep(1);
    setSelectedFaculty(null);
    setSelectedSection(null);
    setSelectedService(null);
    setStudentName('');
    setStudentId('');
    setGeneratedTicket(null);
    setQrCode(null);
  };

  const handleBack = () => {
    if (step === 2) { setSelectedFaculty(null); setSections([]); }
    else if (step === 3) { setSelectedSection(null); setServices([]); }
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="student-kiosk">
      <div className="kiosk-header">
        <h1>BDTU navbat dasturi</h1>
        <p>Iltimos, navbatingizni olish uchun qadamlardan o'ting</p>
      </div>

      <div className="container">
        <div className="card">
          {/* Progress Steps */}
          <div className="progress-steps">
            {['Fakultet', "Bo'lim", 'Xizmat', 'Navbat'].map((label, i) => (
              <div key={i} className={`step ${step >= i + 1 ? 'active' : ''}`}>
                <div className="step-number">{i + 1}</div>
                <div className="step-label">{label}</div>
              </div>
            ))}
          </div>

          {/* Step 1: Faculty */}
          {step === 1 && (
            <div className="step-content">
              <h2>Fakultetni tanlash</h2>
              <div className="grid grid-2 mt-3">
                {faculties.map((f) => (
                  <button key={f.id} className="selection-card" onClick={() => handleFacultySelect(f)}>
                    <div className="selection-code">{f.code}</div>
                    <div className="selection-name">{f.name}</div>
                    {f.description && <div className="selection-desc">{f.description}</div>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Section */}
          {step === 2 && (
            <div className="step-content">
              <h2>Bo'limni tanlash</h2>
              <p className="selected-info">Fakultet: <strong>{selectedFaculty?.name}</strong></p>
              <div className="grid grid-2 mt-3">
                {sections.map((s) => (
                  <button key={s.id} className="selection-card" onClick={() => handleSectionSelect(s)}>
                    <div className="selection-code">{s.code}</div>
                    <div className="selection-name">{s.name}</div>
                    {s.description && <div className="selection-desc">{s.description}</div>}
                  </button>
                ))}
              </div>
              <button className="btn btn-secondary mt-3" onClick={handleBack}>Orqaga</button>
            </div>
          )}

          {/* Step 3: Service */}
          {step === 3 && (
            <div className="step-content">
              <h2>Xizmatni tanlash</h2>
              <p className="selected-info">
                Fakultet: <strong>{selectedFaculty?.name}</strong> | Bo'lim: <strong>{selectedSection?.name}</strong>
              </p>
              <div className="grid grid-2 mt-3">
                {services.map((s) => (
                  <button key={s.id} className="selection-card" onClick={() => handleServiceSelect(s)}>
                    <div className="selection-code">{s.code}</div>
                    <div className="selection-name">{s.name}</div>
                    {s.description && <div className="selection-desc">{s.description}</div>}
                    <div className="selection-time">Vaqt: {s.estimated_time} min</div>
                  </button>
                ))}
              </div>
              <button className="btn btn-secondary mt-3" onClick={handleBack}>Orqaga</button>
            </div>
          )}

          {/* Step 5: QR Code + Print */}
          {step === 5 && generatedTicket && (
            <div className="step-content">
              <div className="qr-display-container">
                <h2>Navbatingiz yaratildi!</h2>
                <p className="qr-instruction">Iltimos, QR kodni skanerlang</p>
                {qrCode && (
                  <div className="qr-code-display">
                    <img src={qrCode} alt="Ticket QR Code" className="qr-code-large" />
                  </div>
                )}
                <div className="qr-info">
                  <p><strong>Navbat raqami:</strong> {generatedTicket.ticket_number}</p>
                  <p className="qr-hint">QR kodni skanerlang va barcha tafsilotlarni ko'ring</p>
                </div>
              </div>
              <div className="button-group mt-3 no-print">
                <button className="btn btn-primary" onClick={handlePrintTicket}>
                  Navbatni chiqarish
                </button>
                <button className="btn btn-secondary" onClick={handleReset}>
                  Boshqa navbat olish
                </button>
              </div>
            </div>
          )}

          {loading && <div className="loading"><p>Kutilmoqda...</p></div>}
        </div>
      </div>
    </div>
  );
}

export default StudentKiosk;
